// Ported from mantis-business-front-brinks's src/components/filtro-columna.tsx — the Excel-style
// per-column filter popover used by the "Panel de cierres" table header. Pure UI, no data
// shape assumptions, so it is reused as-is (only the token names changed — see
// `EstadoBadge.jsx`'s header for why brinks' own names don't resolve here).
import { useMemo, useState } from "react";
import { Filter, Search } from "lucide-react";
import { Checkbox, Popover, PopoverContent, PopoverTrigger, cn } from "@mantis-sas/ui";
import "./styles/brinksTheme.css";

export function FiltroColumna({ titulo, activo, onLimpiar, align = "start", children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filtrar ${titulo}`}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "relative grid h-6 w-6 shrink-0 place-items-center rounded-[6px] text-brinks-muted-foreground/70 transition-colors duration-200 hover:bg-brinks-surface-hover hover:text-brinks-foreground",
            activo && "bg-brinks-surface text-brinks-foreground"
          )}
        >
          <Filter className="h-3.5 w-3.5" />
          {activo && <span className="absolute right-[3px] top-[3px] h-1.5 w-1.5 rounded-full bg-primary" />}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        onClick={(e) => e.stopPropagation()}
        className="brinks-scope font-redhat w-[260px] rounded-[12px] border bg-popover p-3 shadow-lg duration-200"
      >
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.04em] text-brinks-muted-foreground">
            Filtrar {titulo}
          </p>
          {activo && (
            <button
              type="button"
              onClick={onLimpiar}
              className="text-[12px] font-medium text-brinks-muted-foreground transition-colors hover:text-brinks-foreground"
            >
              Limpiar
            </button>
          )}
        </div>
        {children}
      </PopoverContent>
    </Popover>
  );
}

export function FiltroLista({ opciones, seleccionados, onChange, buscable }) {
  const [q, setQ] = useState("");
  const visibles = useMemo(
    () => opciones.filter((o) => o.etiqueta.toLowerCase().includes(q.toLowerCase())),
    [opciones, q]
  );

  const toggle = (v) =>
    onChange(seleccionados.includes(v) ? seleccionados.filter((x) => x !== v) : [...seleccionados, v]);

  return (
    <div>
      {buscable && (
        <span className="relative mb-2 block">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brinks-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar..."
            className="h-9 w-full rounded-[8px] border bg-card pl-8 pr-2.5 text-[13px] outline-none transition focus:border-brinks-border-strong"
          />
        </span>
      )}
      <div className="mb-1.5 flex gap-3 text-[12px]">
        <button
          type="button"
          onClick={() => onChange(opciones.map((o) => o.valor))}
          className="font-medium text-brinks-muted-foreground transition-colors hover:text-brinks-foreground"
        >
          Seleccionar todo
        </button>
        <button
          type="button"
          onClick={() => onChange([])}
          className="font-medium text-brinks-muted-foreground transition-colors hover:text-brinks-foreground"
        >
          Ninguno
        </button>
      </div>
      <div className="max-h-[220px] space-y-0.5 overflow-y-auto">
        {visibles.map((o) => (
          <label
            key={o.valor}
            className="flex cursor-pointer items-center gap-2.5 rounded-[8px] px-1.5 py-1.5 text-[13px] transition-colors hover:bg-brinks-surface-hover"
          >
            <Checkbox checked={seleccionados.includes(o.valor)} onCheckedChange={() => toggle(o.valor)} />
            <span className="truncate">{o.etiqueta}</span>
          </label>
        ))}
        {visibles.length === 0 && (
          <p className="px-1.5 py-3 text-center text-[12px] text-brinks-muted-foreground">Sin resultados.</p>
        )}
      </div>
    </div>
  );
}

export function FiltroRangoNumero({ min, max, onChange, atajos }) {
  return (
    <div>
      {atajos && atajos.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {atajos.map((a) => (
            <button
              key={a.etiqueta}
              type="button"
              onClick={a.onClick}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors duration-200",
                a.activo ? "border-primary bg-primary text-primary-foreground" : "bg-card text-brinks-muted-foreground hover:text-brinks-foreground"
              )}
            >
              {a.etiqueta}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.04em] text-brinks-muted-foreground">
            Mínimo
          </span>
          <input
            type="number"
            value={min}
            onChange={(e) => onChange(e.target.value, max)}
            placeholder="0"
            className="h-9 w-full rounded-[8px] border bg-card px-2.5 text-[13px] tabular-nums outline-none transition focus:border-brinks-border-strong"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.04em] text-brinks-muted-foreground">
            Máximo
          </span>
          <input
            type="number"
            value={max}
            onChange={(e) => onChange(min, e.target.value)}
            placeholder="Sin límite"
            className="h-9 w-full rounded-[8px] border bg-card px-2.5 text-[13px] tabular-nums outline-none transition focus:border-brinks-border-strong"
          />
        </label>
      </div>
    </div>
  );
}

export function FiltroRangoFechas({ desde, hasta, onChange, atajos }) {
  return (
    <div>
      {atajos && atajos.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {atajos.map((a) => (
            <button
              key={a.etiqueta}
              type="button"
              onClick={a.onClick}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[12px] font-medium transition-colors duration-200",
                a.activo ? "border-primary bg-primary text-primary-foreground" : "bg-card text-brinks-muted-foreground hover:text-brinks-foreground"
              )}
            >
              {a.etiqueta}
            </button>
          ))}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.04em] text-brinks-muted-foreground">
            Desde
          </span>
          <input
            type="date"
            value={desde}
            onChange={(e) => onChange(e.target.value, hasta)}
            className="h-9 w-full rounded-[8px] border bg-card px-2.5 text-[13px] tabular-nums outline-none transition focus:border-brinks-border-strong"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-[11px] font-medium uppercase tracking-[0.04em] text-brinks-muted-foreground">
            Hasta
          </span>
          <input
            type="date"
            value={hasta}
            onChange={(e) => onChange(desde, e.target.value)}
            className="h-9 w-full rounded-[8px] border bg-card px-2.5 text-[13px] tabular-nums outline-none transition focus:border-brinks-border-strong"
          />
        </label>
      </div>
    </div>
  );
}
